const LOCAL_URL_PATTERN = /(?:^|[\s"'=>(])(?:blob:|file:)/i
const MAX_IMAGE_DIMENSION = 2400
const WEBP_QUALITY = 0.88

type DecodedImage = {
  source: CanvasImageSource
  width: number
  height: number
  close?: () => void
}

async function decodeImage(file: File): Promise<DecodedImage> {
  if (typeof createImageBitmap === "function") {
    const bitmap = await createImageBitmap(file)
    return {
      source: bitmap,
      width: bitmap.width,
      height: bitmap.height,
      close: () => bitmap.close(),
    }
  }

  const objectUrl = URL.createObjectURL(file)
  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const element = new Image()
      element.onload = () => resolve(element)
      element.onerror = () => reject(new Error("Unable to decode image"))
      element.src = objectUrl
    })
    return {
      source: image,
      width: image.naturalWidth,
      height: image.naturalHeight,
    }
  } finally {
    URL.revokeObjectURL(objectUrl)
  }
}

export async function optimizeImageForUpload(file: File): Promise<File> {
  if (!file.type.startsWith("image/") || file.type === "image/gif") {
    return file
  }

  let decoded: DecodedImage
  try {
    decoded = await decodeImage(file)
  } catch {
    return file
  }

  const scale = Math.min(
    1,
    MAX_IMAGE_DIMENSION / decoded.width,
    MAX_IMAGE_DIMENSION / decoded.height,
  )
  const width = Math.max(1, Math.round(decoded.width * scale))
  const height = Math.max(1, Math.round(decoded.height * scale))
  const canvas = document.createElement("canvas")
  canvas.width = width
  canvas.height = height

  try {
    const context = canvas.getContext("2d")
    if (!context) {
      return file
    }

    context.drawImage(decoded.source, 0, 0, width, height)
    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, "image/webp", WEBP_QUALITY)
    })
    if (!blob) {
      return file
    }

    const basename = file.name.replace(/\.[^/.]+$/, "") || "image"
    return new File([blob], `${basename}.webp`, {
      type: "image/webp",
      lastModified: file.lastModified,
    })
  } finally {
    decoded.close?.()
  }
}

export function containsLocalImageUrls(
  html: string | null | undefined,
): boolean {
  if (!html) {
    return false
  }

  return LOCAL_URL_PATTERN.test(html)
}

export async function replaceLocalImageUrlsWithUploads(
  html: string,
  filesByUrl: Map<string, File>,
  uploadImage: (file: File) => Promise<string>,
): Promise<string> {
  if (!containsLocalImageUrls(html)) {
    return html
  }

  const container = document.createElement("div")
  container.innerHTML = html

  for (const image of Array.from(container.querySelectorAll("img"))) {
    const src = image.getAttribute("src")
    if (!src || !containsLocalImageUrls(src)) {
      continue
    }

    const file = filesByUrl.get(src)
    if (!file) {
      continue
    }

    image.setAttribute("src", await uploadImage(file))
  }

  return container.innerHTML
}
